/**
 * UserPresenceDot · v2.7.6
 * ────────────────────────
 * Tiny live dot indicator that renders next to a staff member's name in
 * the User Management roster. Backed by the shared `useAdminPresence`
 * hook → 30 s heartbeat-driven status.
 *
 * - Online  → pulsing emerald 6×6px dot
 * - Offline → solid zinc 6×6px dot
 *
 * The username should match the user's login identifier (the same key
 * the backend uses for `admin_profiles._id`). For driver records, that's
 * typically the `email` field; fall back to `username`.
 */
import { usePresenceFor } from "@/hooks/useAdminPresence";


export default function UserPresenceDot({ username, size = 8 }) {
  const entry = usePresenceFor(username);
  const isOnline = entry?.status === "online";
  const px = `${size}px`;
  return (
    <span
      className="relative inline-flex items-center justify-center flex-shrink-0"
      style={{ height: px, width: px }}
      data-testid={`presence-dot-${username || "anon"}`}
      data-status={isOnline ? "online" : "offline"}
      title={isOnline
        ? `Online · last seen ${entry?.last_seen_at ? new Date(entry.last_seen_at).toLocaleTimeString() : "now"}`
        : "Offline"}
    >
      <span
        className={`rounded-full ${isOnline ? "bg-emerald-400 admin-status-pulse" : "bg-zinc-600"}`}
        style={{ height: px, width: px }}
      />
    </span>
  );
}
